import { useMemo } from 'react';
import { useAuthStore } from '@/stores/useAuthStore';
import { DESIGN_ROLES, type DesignRoleId } from '@/config/designRoles';

/**
 * WHO IS LOOKING, AND FROM WHERE — read from the session, never assumed.
 *
 * Every design screen needs the same three facts: the person in the rail foot,
 * the branch beside their role, and which role's doors to draw. Deriving that
 * per page is how the three drift apart, and each of them had already been
 * hardcoded somewhere:
 *
 *   - the rail foot defaulted to "Maadi ▾" — the canvas's branch, printed for
 *     every clinic on earth;
 *   - the doctor screens asked for `role="doctor"` outright, so a nurse opening
 *     one would have been handed a doctor's five doors;
 *   - the roster in `designRoles.ts` (Dr. Mostafa Adam, Salma Adel …) is the
 *     CANVAS's cast. It belongs in the gallery and nowhere near a real screen.
 *
 * A clinician seeing another clinic's branch, or a role's doors they do not
 * hold, is not a cosmetic bug.
 */

/** Which backend role name lands on which design surface. */
const SURFACE_FOR: Record<string, DesignRoleId> = {
  Doctor: 'doctor',
  Owner: 'doctor',
  'Clinic Manager': 'clinic-manager',
  Nurse: 'nurse',
  Receptionist: 'reception',
  Billing: 'reception',
  'Stock Manager': 'stock-manager',
  Accountant: 'accountant',
  Technician: 'technician',
};

export interface ShellIdentity {
  /** The rail foot. Null before the session resolves. */
  person: { name: string; initials: string; role?: string } | null;
  /** The branch label, already carrying the design's ▾. */
  branch: string;
  /** Which role's doors to draw. */
  surface: DesignRoleId;
  /** True when this person's role has no design surface of its own. */
  surfaceIsFallback: boolean;
}

/**
 * DEVELOPMENT ONLY — view any role's surface: `?surface=accountant`.
 *
 * Two of the design's roles (accountant, technician) still have no backend
 * role to sign in as, so without this their screens could be built and never
 * looked at. Kept for the tab in sessionStorage so navigating keeps
 * it; `?surface=` with no value clears it. Compiled out of production builds —
 * `import.meta.env.DEV` is false there, so a real user can never be shown
 * another role's doors by a query string.
 */
function devSurfaceOverride(): DesignRoleId | null {
  if (!import.meta.env.DEV) return null;
  try {
    const q = new URLSearchParams(window.location.search);
    if (q.has('surface')) {
      const v = q.get('surface') || '';
      if (v) sessionStorage.setItem('maie-dev-surface', v);
      else sessionStorage.removeItem('maie-dev-surface');
    }
    const v = sessionStorage.getItem('maie-dev-surface');
    return v && v in DESIGN_ROLES ? (v as DesignRoleId) : null;
  } catch {
    return null;
  }
}

export function useShellIdentity(): ShellIdentity {
  const user = useAuthStore((s) => s.user);
  const tenant = useAuthStore((s) => s.activeTenant);

  return useMemo(() => {
    const roleName = tenant?.role ?? '';
    const mapped = devSurfaceOverride() ?? SURFACE_FOR[roleName];

    /* THE BOARDS READ "Dr. Mostafa Adam". A clinician's title is part of how
       a clinic addresses them, and the rail foot is where the person is named;
       the desk and the nurse keep their plain names, as their own boards do. */
    const clinician = mapped === 'doctor';
    const name =
      [clinician ? 'Dr.' : null, user?.firstName, user?.lastName]
        .filter(Boolean)
        .join(' ')
        .trim() ||
      user?.username ||
      '';
    const initials =
      `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase() ||
      (user?.username?.[0]?.toUpperCase() ?? '');

    return {
      person: user ? { name, initials, role: roleName || undefined } : null,
      /* The tenant's own display name. Falls back to the generic word rather
         than to a branch name from somewhere else — "Clinic" is obviously a
         placeholder; "Maadi" reads as a fact. */
      branch: tenant?.displayName ? `${tenant.displayName} ▾` : 'Clinic',
      surface: mapped ?? 'doctor',
      surfaceIsFallback: !mapped,
    };
  }, [user, tenant]);
}
